import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { take, map, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { DropdownOption } from '../dropdown/dropdown.component';
import { PlanService, PlanDetailsResponse, PlanDropdownsResponse, PlanUpdateRequest } from '../../../core/services/plan.service';
import { PatientService } from '../../../core/services/patient.service';

@Component({
  selector: 'app-plan-popup',
  standalone: false,
  templateUrl: './plan-popup.component.html',
  styleUrl: './plan-popup.component.css'
})
export class PlanPopupComponent implements OnInit, OnChanges {
  /** Optional: existing plan ID to load for Edit mode. When set, GET /plans/{planId} is called and response is displayed. */
  @Input() planId: number | null = null;
  @Input() planName: string = '';
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<Record<string, unknown>>();

  form!: FormGroup;
  anesthesiaLinkOptions: DropdownOption[] = [];
  formOptions: DropdownOption[] = [];
  insuranceOptions: DropdownOption[] = [];
  categoryOptions: DropdownOption[] = [];
  submitVendorOptions: DropdownOption[] = [];
  planTypeOptions: DropdownOption[] = [];
  
  isSaving: boolean = false;
  saveError: string | null = null;
  showSaveSuccessPopup: boolean = false;

  /** Loading and error state for GET /plans/{planId} */
  isLoadingPlan: boolean = false;
  loadPlanError: string | null = null;

  dismissSaveError(): void {
    this.saveError = null;
  }

  claimFlagOptions = [
    'Medicare', 'Medicaid', 'Champus', 'Champva', 'Group', 'Others', 'FECA', 'BC/BS', 'GHI Medicare', 'Fidelis', 'HealthFirst'
  ];
  claimOptionLabels = ['Reserved Field 24 K', 'Secondary Bill Allowed', 'Anesthesia Bill Minutes'];

  constructor(
    private fb: FormBuilder,
    private planService: PlanService,
    private patientService: PatientService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      planName: [this.planName || '', Validators.required],
      description: [''],
      contact: [''],
      insurance: ['', Validators.required],
      category: ['', Validators.required],
      comments: [''],
      outstandingDays: [30],
      anesthesiaLink: [''],
      submitVendor: [''],
      active: [true],
      managedCare: [false],
      capitated: [false],
      capAutoWriteoff: [false],
      electSubmit: [true],
      elecEobWriteoff: [false],
      pinRelation: [false],
      form: ['XML HCFA'],
      planType: ['medical'],
      statementComments: [''],
      address: [''],
      city: [''],
      state: [''],
      zip: [''],
      zipExt: [''],
      addressContact: [''],
      tel: [''],
      telExt: [''],
      fax: [''],
      claimFlagOthers: [true],
      claimFlagMedicare: [false],
      claimFlagMedicaid: [false],
      claimFlagChampus: [false],
      claimFlagChampva: [false],
      claimFlagGroup: [false],
      claimFlagFECA: [false],
      claimFlagBCBS: [false],
      claimFlagGHIMedicare: [false],
      claimFlagFidelis: [false],
      claimFlagHealthFirst: [false],
      claimOptReserved: [false],
      claimOptSecondaryBill: [false],
      claimOptAnesthesiaBill: [false]
    });
    if (this.planName) {
      this.form.patchValue({ planName: this.planName });
    }

    // Load dropdowns and plan details in parallel for faster initialization
    // The modal opens immediately while data loads in the background
    this.loadDropdowns().subscribe({
      next: () => {
        // Dropdowns loaded - trigger change detection
        this.cdr.markForCheck();
      },
      error: () => {
        // Continue even if dropdowns fail
        this.cdr.markForCheck();
      }
    });

    // Load plan details immediately (don't wait for dropdowns)
    // Form values will be patched once both are ready
    this.tryLoadPlanDetails();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const planIdChange = changes['planId'];
    if (!planIdChange || !this.form) return;
    const id = this.resolvePlanId(this.planId);
    // When planId becomes available after popup is open, trigger GET /plans/{planId}.
    if (id != null && !planIdChange.firstChange) {
      this.loadPlanDetails(id);
    }
  }

  /** Resolve planId to a positive number (handles number or string from parent). */
  private resolvePlanId(value: number | string | null | undefined): number | null {
    if (value == null || value === '') return null;
    const n = Number(value);
    return !isNaN(n) && n > 0 ? n : null;
  }

  /**
   * Trigger GET /plans/{planId} when popup opens.
   * Resolves planId from: (1) @Input() planId, (2) route query param planId, (3) plan name search when only planName is available.
   */
  private tryLoadPlanDetails(): void {
    const id = this.resolvePlanId(this.planId);
    if (id != null) {
      this.loadPlanDetails(id);
      return;
    }
    // Fallback 1: planId from URL query (e.g. ?planId=123)
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      const fromRoute = this.resolvePlanId(params['planId'] ?? null);
      if (fromRoute != null) {
        (this as { planId: number | null }).planId = fromRoute;
        this.loadPlanDetails(fromRoute);
        this.cdr.markForCheck();
        return;
      }
      // Fallback 2: resolve planId from planName via plan search (so GET /plans/{planId} is always triggered when plan name is shown)
      const name = (this.planName || '').trim();
      if (name.length > 0) {
        this.patientService.searchPlans(name).pipe(take(1)).subscribe(res => {
          const plans = res?.plans ?? [];
          const match = plans.find(p => (String(p.planName || '').trim()).toLowerCase() === name.toLowerCase()) || plans[0];
          if (match?.planId != null && match.planId > 0) {
            (this as { planId: number | null }).planId = match.planId;
            this.loadPlanDetails(match.planId);
            this.cdr.markForCheck();
          }
        });
      }
    });
  }

  onCancel(): void {
    this.close.emit();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });
      this.saveError = 'Please fill in all required fields (Plan Name, Insurance, and Category).';
      this.cdr.detectChanges();
      return;
    }

    if (this.isSaving) return;

    const formValue = this.form.getRawValue();

    // If planId exists, use PUT API to update
    if (this.planId != null && this.planId > 0) {
      this.isSaving = true;
      this.saveError = null;
      
      const updateBody = this.buildPlanUpdateBody(formValue);
      
      this.planService.updatePlan(this.planId, updateBody).subscribe({
        next: () => {
          this.isSaving = false;
          this.saveError = null;
          this.showSaveSuccessPopup = true;
          this.save.emit(formValue);
          this.cdr.detectChanges();
        },
        error: (err: { status?: number; message?: string }) => {
          this.isSaving = false;
          const status = err?.status;
          const msg = err?.message || 'Failed to update plan.';
          if (status === 400) {
            this.saveError = 'Validation error. ' + (err?.message || 'Please check the form and try again.');
          } else if (status === 401) {
            this.saveError = 'Unauthorized. Please sign in again.';
          } else if (status === 404) {
            this.saveError = 'Plan not found. Cannot update.';
          } else if (status === 500) {
            this.saveError = 'Server error. Please try again later.';
          } else {
            this.saveError = msg;
          }
          this.cdr.detectChanges();
        }
      });
    } else {
      // For new plans (no planId), emit the form value as before
      this.save.emit(formValue);
      this.close.emit();
    }
  }

  /**
   * Load existing plan details for Add/Edit Plan popup.
   * Endpoint: GET /plans/{planId} — response data is mapped and displayed in the form.
   */
  private loadPlanDetails(planId: number): void {
    this.isLoadingPlan = true;
    this.loadPlanError = null;
    this.cdr.markForCheck();

    this.planService
      .getPlanDetails(planId)
      .subscribe({
        next: (details: PlanDetailsResponse) => {
          this.isLoadingPlan = false;
          this.loadPlanError = null;
          if (!details) {
            this.cdr.markForCheck();
            return;
          }

          // Patch form values - use requestAnimationFrame for smooth UI update
          // This ensures dropdown options are processed by Angular before setting values
          requestAnimationFrame(() => {
            this.form.patchValue({
              planName: details.planName ?? this.form.get('planName')?.value,
              description: details.planDescription ?? '',
              contact: details.planContact ?? '',
              insurance: details.insuranceId ?? '',
              category: details.planCategoryId ?? '',
              comments: details.planComments ?? '',
              outstandingDays: details.planOutstandingDays ?? 30,
              anesthesiaLink: details.planAnesthesiaLink ?? '',
              submitVendor: details.planSubmitVendorId ?? '',
              active: !!details.planActive,
              managedCare: !!details.planManagedCare,
              capitated: !!details.planCapitated,
              capAutoWriteoff: !!details.planCapAutoWriteOff,
              electSubmit: !!details.planElectSubmit,
              elecEobWriteoff: !!details.planElectEOBWriteOff,
              pinRelation: !!details.planPINRelation,
              form: details.planFormId ?? this.form.get('form')?.value,
              planType: this.mapPlanTypeIdToValue(details.planTypeId),
              statementComments: details.planStatementComments ?? '',
              address: details.planAddress ?? '',
              city: details.planCity ?? '',
              state: details.planState ?? '',
              zip: details.planZipCode ?? '',
              zipExt: details.planExt ?? '',
              addressContact: details.planAddressContact ?? '',
              tel: details.planTel ?? '',
              telExt: details.planTelExt ?? '',
              fax: details.planFax ?? '',
              claimFlagMedicare: !!details.planMedicare,
              claimFlagMedicaid: !!details.planMedicaid,
              claimFlagChampus: !!details.planChampus,
              claimFlagChampva: !!details.planChampVa,
              claimFlagGroup: !!details.planGroup,
              claimFlagOthers: !!details.planOther,
              claimFlagFECA: !!details.planFeca,
              claimFlagBCBS: !!details.planBcbs,
              claimFlagGHIMedicare: !!details.planGhiMedicare,
              claimFlagFidelis: !!details.planFidelis,
              claimFlagHealthFirst: !!details.planHealthFirst,
              claimOptReserved: !!details.planReservedField24k,
              claimOptSecondaryBill: !!details.planSecondaryBillAllowed,
              claimOptAnesthesiaBill: !!details.planAnesthesiaBillMinutes
            }, { emitEvent: false });
            this.cdr.detectChanges();
          });
        },
        error: (err: { status?: number; message?: string }) => {
          this.isLoadingPlan = false;
          this.loadPlanError = err?.message || 'Failed to load plan details.';
          this.cdr.markForCheck();
        }
      });
  }

  dismissLoadPlanError(): void {
    this.loadPlanError = null;
    this.cdr.markForCheck();
  }

  /** Map numeric planTypeId from API to the string value used by the radio group. */
  private mapPlanTypeIdToValue(planTypeId?: number): string {
    if (planTypeId === 2) {
      return 'no-fault';
    }
    if (planTypeId === 3) {
      return 'wc';
    }
    // Default to Medical
    return 'medical';
  }

  /** Map string planType value from form to numeric ID for API. */
  private mapPlanTypeValueToId(planType?: string): number {
    if (planType === 'no-fault') {
      return 2;
    }
    if (planType === 'wc') {
      return 3;
    }
    // Default to Medical (1)
    return 1;
  }

  /**
   * Build the API request body from form values.
   * Maps form field names to API field names and converts types as needed.
   */
  private buildPlanUpdateBody(formValue: Record<string, unknown>): PlanUpdateRequest {
    const num = (x: any): number => {
      if (x == null || x === '') return 0;
      const n = typeof x === 'number' ? x : parseFloat(String(x));
      return isNaN(n) ? 0 : n;
    };

    const str = (x: any): string => (x != null && x !== '') ? String(x).trim() : '';

    return {
      insuranceId: formValue['insurance'] ? num(formValue['insurance']) : 0,
      planActive: !!formValue['active'],
      planAddress: str(formValue['address']),
      planAddressContact: str(formValue['addressContact']),
      planAnesthesiaBillMinutes: !!formValue['claimOptAnesthesiaBill'],
      planAnesthesiaLink: formValue['anesthesiaLink'] ? num(formValue['anesthesiaLink']) : 0,
      planBcbs: !!formValue['claimFlagBCBS'],
      planCapAutoWriteOff: !!formValue['capAutoWriteoff'],
      planCapitated: !!formValue['capitated'],
      planCategoryId: formValue['category'] ? num(formValue['category']) : 0,
      planChampVa: !!formValue['claimFlagChampva'],
      planChampus: !!formValue['claimFlagChampus'],
      planCity: str(formValue['city']),
      planComments: str(formValue['comments']),
      planContact: str(formValue['contact']),
      planDescription: str(formValue['description']),
      planElectEobWriteOff: !!formValue['elecEobWriteoff'],
      planElectSubmit: !!formValue['electSubmit'],
      planExt: str(formValue['zipExt']),
      planFax: str(formValue['fax']),
      planFeca: !!formValue['claimFlagFECA'],
      planFidelis: !!formValue['claimFlagFidelis'],
      planFormId: formValue['form'] ? num(formValue['form']) : 0,
      planGhiMedicare: !!formValue['claimFlagGHIMedicare'],
      planGroup: !!formValue['claimFlagGroup'],
      planHealthFirst: !!formValue['claimFlagHealthFirst'],
      planManagedCare: !!formValue['managedCare'],
      planMedicaid: !!formValue['claimFlagMedicaid'],
      planMedicare: !!formValue['claimFlagMedicare'],
      planName: str(formValue['planName']),
      planOther: !!formValue['claimFlagOthers'],
      planOutstandingDays: formValue['outstandingDays'] ? num(formValue['outstandingDays']) : 0,
      planPinRelation: !!formValue['pinRelation'],
      planReservedField24k: !!formValue['claimOptReserved'],
      planSecondaryBillAllowed: !!formValue['claimOptSecondaryBill'],
      planState: str(formValue['state']),
      planStatementComments: str(formValue['statementComments']),
      planSubmitVendorId: formValue['submitVendor'] ? num(formValue['submitVendor']) : 0,
      planTel: str(formValue['tel']),
      planTelExt: str(formValue['telExt']),
      planTypeId: this.mapPlanTypeValueToId(formValue['planType'] as string),
      planZipCode: str(formValue['zip'])
    };
  }

  /**
   * Load dropdown options for the Plan popup from backend.
   * Endpoint: GET /plans/dropdowns?dropdown=insurance,category,submitVendor,form,anesthesia,planType
   * Returns an Observable so callers can wait for dropdowns to load before setting form values.
   */
  private loadDropdowns(): Observable<void> {
    return this.planService
      .getPlanDropdowns(['insurance', 'category', 'submitVendor', 'form', 'anesthesia', 'planType'])
      .pipe(
        map((payload: PlanDropdownsResponse) => {
          // Helper function to safely map dropdown options, filtering out invalid entries
          const mapOptions = (options: any[] | undefined): DropdownOption[] => {
            if (!options || !Array.isArray(options)) {
              return [];
            }
            return options
              .filter((opt: any) => opt && (opt.id !== undefined && opt.id !== null) && opt.label)
              .map((opt: { id: number; label: string }) => ({
                label: opt.label || '',
                value: opt.id
              }));
          };
          
          // Map anesthesia options
          this.anesthesiaLinkOptions = mapOptions(payload?.anesthesia);
          
          // Map form options
          this.formOptions = mapOptions(payload?.form);
          
          // Map insurance options
          this.insuranceOptions = mapOptions(payload?.insurance);
          
          // Map category options
          this.categoryOptions = mapOptions(payload?.category);
          
          // Map submit vendor options
          this.submitVendorOptions = mapOptions(payload?.submitVendor);
          
          // Map plan type options
          this.planTypeOptions = mapOptions(payload?.planType);
          
          // Trigger change detection to ensure dropdowns are updated in the UI
          this.cdr.markForCheck();
        }),
        catchError((_err: { status?: number; message?: string }) => {
          // On error, keep dropdowns empty; UI will still render correctly. Error handled by caller if needed.
          return of(undefined);
        })
      );
  }
}
